import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

import type {
    CrmEnrichmentJobRunHistoryItem,
    CrmEnrichmentJobRunHistoryResponse,
    CrmEnrichmentJobType,
} from "../../types/types";

import { getEnrichmentLogsColumns } from "./columns";
import { StyledTableContainerFlex } from "./styles";

const TABLE_STYLES = { minWidth: "1000px" };

type Props = {
    integrationName: Integration;
    data: CrmEnrichmentJobRunHistoryResponse | undefined;
    isLoading: boolean;
    page: number;
    pageSize: number;
    jobType: CrmEnrichmentJobType | undefined;
    onPageChange: (currentPage: number, pageSize: number, nextPage: number) => void;
    onDownloadCsv: (runId: string) => void;
};

const EnrichmentLogsTable = ({
    integrationName,
    data,
    isLoading,
    page,
    pageSize,
    jobType,
    onPageChange,
    onDownloadCsv,
}: Props) => {
    const { translate } = useTranslate();

    const isShowExternalId = jobType !== "instant";

    const columnsConfig = useMemo(
        () => getEnrichmentLogsColumns(onDownloadCsv, integrationName, isShowExternalId),
        [onDownloadCsv, isShowExternalId, integrationName],
    );

    return (
        <StyledTableContainerFlex
            flexDirection="column"
            gap="16px"
        >
            <Text
                fontVariant="h5"
                color={colorsPalettes.carbon["500"]}
            >
                {translate("si.crm-enrichment.logs.run-history")}
            </Text>

            {data && data.records ? (
                <Table
                    isLoading={isLoading}
                    data={data.records as CrmEnrichmentJobRunHistoryItem[]}
                    columnConfig={columnsConfig}
                    pagination={{
                        pageSize,
                        total: data.totalCount,
                        current: page,
                        onChange: onPageChange,
                    }}
                    style={TABLE_STYLES}
                    noData={{
                        type: "no-data" as const,
                        title: translate("si.crm-enrichment.logs.no-data"),
                    }}
                    bordersConfig={{ body: false, header: false }}
                    edgeColumnsPadding="md"
                    withArrowButtons={false}
                />
            ) : null}
        </StyledTableContainerFlex>
    );
};

export default EnrichmentLogsTable;
